import { visit } from 'unist-util-visit';

// Helper: force conversion of any markdown lines starting with "Download:" or "Downloads:" into a consistently styled pseudo-element.
// This complements explicit analysisfile links. It wraps the text in a span we can target and upgrade to a button in React.
// SKIP transformation if followed by analysisfile links (which already render their own download buttons).
function transformDownloadLines(tree: any) {
  visit(tree, (node: any, idx: number | undefined, parent: any) => {
    if (typeof idx !== 'number' || !parent) return;
    if (!node || node.type !== 'element') return;
    if (node.tagName === 'p' && Array.isArray(node.children) && node.children.length > 0) {
      const first = node.children[0];
      const textContent = first && first.type === 'text' ? (first.value || '').trim() : '';
      // Match "Download:" or "Downloads:" (with optional 's')
      if (/^Downloads?\s*:/i.test(textContent)) {
        // Check if ANY following sibling contains an analysisfile-link (avoid duplicate buttons)
        // Look through subsequent siblings (typically list items) for analysisfile-link elements
        let hasAnalysisfileLink = false;
        
        // ENHANCED: Also check if THIS paragraph contains an analysisfile-link on the same line
        const checkForAnalysisLink = (el: any): boolean => {
          if (el.tagName === 'analysisfile-link') return true;
          if (el.children && Array.isArray(el.children)) {
            return el.children.some((child: any) => 
              child.type === 'element' && checkForAnalysisLink(child)
            );
          }
          return false;
        };
        
        // Check current node first (for inline links after "Download:")
        if (checkForAnalysisLink(node)) {
          hasAnalysisfileLink = true;
        }
        
        // Then check following siblings
        if (!hasAnalysisfileLink) {
          for (let i = idx + 1; i < parent.children.length && i < idx + 10; i++) {
            const sibling = parent.children[i];
            if (sibling && sibling.type === 'element') {
              if (checkForAnalysisLink(sibling)) {
                hasAnalysisfileLink = true;
                break;
              }
            }
          }
        }
        
        if (hasAnalysisfileLink) {
          // Skip creating download-block, the analysisfile-link will handle it
          return;
        }
        
        const fullText = node.children.map((c: any) => c.type === 'text' ? (c.value || '') : '').join('').trim();
        const label = fullText.replace(/^Downloads?\s*:/i, '').trim() || 'Download';
        parent.children[idx] = {
          type: 'element',
          tagName: 'download-block',
          properties: { 'data-label': label },
          children: []
        };
      }
    }
  });
}

// Rehype plugin: normalize & transform analysisfile links to custom element for consistent button styling.
// Robust against stray whitespace after scheme, extra spaces around colons, and mixed casing.
export default function rehypeAnalysisfile() {
  return (tree: any) => {
    // First transform explicit analysisfile links
    visit(tree, (node: any) => {
      if (!node || node.type !== 'element' || node.tagName !== 'a') return;
      const href: unknown = node.properties?.href;
      if (typeof href !== 'string') return;
      if (!/^analysisfile:/i.test(href)) return;
      const cleanedHref = href
        .replace(/^analysisfile:\s*/i, 'analysisfile:')
        .replace(/\s*:\s*/g, ':')
        .trim();
      const raw = cleanedHref.replace(/^analysisfile:/i, '').trim();
      const normalizedRaw = raw.replace(/\s+/g, ' ');
      const segments = normalizedRaw.split(':');
      if (segments.length < 3) return;
      const containerId = (segments[0] || '').trim();
      const fileId = (segments[1] || '').trim();
      const filenameRaw = segments.slice(2).join(':').trim();
      if (!containerId || !fileId || !filenameRaw) return;
      node.tagName = 'analysisfile-link';
      node.properties['data-container'] = containerId;
      node.properties['data-file'] = fileId;
      node.properties['data-name'] = encodeURIComponent(filenameRaw);
      node.properties['data-origin-href'] = href;
      delete node.properties.href;
    });
    // Then transform "Download:" lines to ensure button rendering consistency.
    // This runs AFTER analysisfile link transformation so we can detect and skip duplicates.
    transformDownloadLines(tree);
  };
}
